import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Updates condition label and description when the variant changes.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} description
 *
 * @extends {Component<Refs>}
 */
class ProductConditionCard extends Component {
  /** @type {Record<string, string> | null} */
  #descriptionsByValue = null;

  /** @type {number | null} */
  #optionIndex = null;

  connectedCallback() {
    super.connectedCallback();
    this.#optionIndex = this.#parseOptionIndex();
    this.#descriptionsByValue = this.#parseDescriptionsByValue();
    this.#headingPrefix = this.dataset.headingPrefix ?? 'Condition: ';
    this.#headingEl = this.querySelector('.product-detail-info-card__heading');

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    target?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @type {HTMLElement | null} */
  #headingEl = null;

  /** @type {string} */
  #headingPrefix = 'Condition: ';

  #parseOptionIndex() {
    const raw = this.dataset.optionIndex;
    if (raw === '' || raw === undefined) return null;
    const index = Number.parseInt(raw, 10);
    return Number.isNaN(index) ? null : index;
  }

  #parseDescriptionsByValue() {
    if (this.dataset.useValueDescriptions !== 'true') {
      return null;
    }

    const jsonEl = this.querySelector('script[type="application/json"][data-descriptions-by-value]');
    if (!jsonEl?.textContent) return null;

    try {
      return JSON.parse(jsonEl.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail?.resource;
    if (!variant) return;

    const conditionLabel = this.#getConditionLabel(variant);
    if (conditionLabel && this.#headingEl) {
      this.#headingEl.textContent = `${this.#headingPrefix}${conditionLabel}`;
    }

    if (!this.refs.description) return;

    const description = this.#getDescriptionForLabel(conditionLabel);
    if (description) {
      this.refs.description.textContent = description;
    }
  };

  /**
   * @param {Object} variant
   * @returns {string}
   */
  #getConditionLabel(variant) {
    if (this.#optionIndex == null) return '';

    const options = variant.options ?? [
      variant.option1,
      variant.option2,
      variant.option3,
    ].filter(Boolean);

    return options[this.#optionIndex] ?? '';
  }

  /**
   * @param {string} label
   * @returns {string | null}
   */
  #getDescriptionForLabel(label) {
    if (!label || !this.#descriptionsByValue) return null;
    return this.#descriptionsByValue[label] ?? null;
  }
}

if (!customElements.get('product-condition-card')) {
  customElements.define('product-condition-card', ProductConditionCard);
}
